import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid } from "recharts";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct } from "./theme";
import { SkeletonCard, NoData, SourceChip, SectionTitle, SrTable,
  ExperimentalBadge, ChartTooltipContent } from "./components";
import { regionName } from "./regionNames";
import { pulseGet } from "./api";

/** P4 — Platform economy across Italian regions: Eurostat tour_ce_omn12
 *  (experimental, NUTS2) vs ISTAT total nights — DECLARED indicative
 *  cross-source ratio, both sources chipped, standard scope caveat. */
export default function ItalyPlatformsPanel({ dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    pulseGet("italy/platforms")
      .then((d) => { if (on) setData(d); })
      .catch(() => { if (on) setData(null); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  const rows = (data?.rows || []).map((r) => ({
    ...r, name: regionName(r.code, lang, r.label),
  }));

  return (
    <section className="mb-10" data-testid="italy-platforms-panel" aria-labelledby="italy-p4-title">
      <SectionTitle dm={dm} right={<ExperimentalBadge dm={dm} />}>
        <span id="italy-p4-title">{t("it_p4_title", { year: data?.year || "" })}</span>
      </SectionTitle>
      <p className="text-xs mb-3 max-w-3xl" style={{ color: T.muted }} data-testid="italy-platforms-caveat">
        {t("it_p4_caveat")}
      </p>
      {loading && <SkeletonCard h={340} dm={dm} />}
      {!loading && !rows.length && <NoData dm={dm} h={160} testid="italy-platforms-no-data" />}
      {!loading && rows.length > 0 && (
        <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }}
          data-testid="italy-platforms-chart">
          <div style={{ height: Math.max(280, rows.length * 24) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 24, bottom: 0, left: 8 }}>
                <CartesianGrid stroke={T.border} horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => fmtPct(v, lang, false)}
                  tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={{ stroke: T.border }} />
                <YAxis type="category" dataKey="name" width={130}
                  tick={{ fontSize: 10, fill: T.text }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtPct(v, lang, false)} />} />
                <Bar dataKey="intensity_pct" name={t("it_p4_intensity")} fill="#F59E0B"
                  isAnimationActive animationDuration={400} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="italy-platforms-chips">
            <SourceChip source="EUROSTAT" dataset="tour_ce_omn12" period={String(data.year)}
              experimental dm={dm} testid="italy-platforms-source-estat" />
            <SourceChip source="ISTAT" dataset="122_54_DF_DCSC_TUR_3" dm={dm}
              testid="italy-platforms-source-istat" />
          </div>
          <SrTable caption={t("it_p4_title", { year: data.year })}
            columns={[t("it_col_region"), t("it_p4_col_platform"), t("it_p4_col_total"), t("it_p4_intensity"), t("yoy")]}
            rows={rows.map((r) => [r.name, fmtCompact(r.platform_nights, lang),
              fmtCompact(r.total_nights_istat, lang), fmtPct(r.intensity_pct, lang, false),
              fmtPct(r.platform_yoy_pct, lang)])} />
        </div>
      )}
    </section>
  );
}
